import CableApp from 'actioncable'
import { withChannelAuth } from 'state/channels/channelAuth'
import type { OrdinalStateEvent } from 'state/ordinalStatePatch'

/**
 * Cable plumbing for live ordinal state (docs/ordinal-state-events.md §5.1).
 *
 * One refcounted `OrdinalsChannel` subscription per tab no matter how many
 * ordinal surfaces are mounted — the same shape `state/feedTicks.ts` uses for
 * `PublicFeedChannel`. The per-user lane rides the existing EventBus service,
 * so nothing here needs its own authenticated socket.
 */

type StateListener = (event: OrdinalStateEvent) => void
type ResyncListener = () => void

const STATE_MESSAGE = 'ordinal state'

const stateListeners = new Set<StateListener>()
const resyncListeners = new Set<ResyncListener>()
const connectionListeners = new Set<(connected: boolean) => void>()

let subscription: { unsubscribe: () => void } | null = null
let connected = false

/**
 * Whether live state is currently arriving. Surfaces that would otherwise poll
 * use this to fall back only while the socket is actually down.
 */
export function isOrdinalStateConnected(): boolean {
    return connected
}

export function subscribeOrdinalStateConnection(
    listener: (connected: boolean) => void,
): () => void {
    connectionListeners.add(listener)
    ensureSubscription()
    listener(connected)
    return () => {
        connectionListeners.delete(listener)
        teardownIfIdle()
    }
}

function setConnected(next: boolean) {
    if (connected === next) return
    connected = next
    connectionListeners.forEach(listener => {
        try {
            listener(next)
        } catch (error) {
            console.error('>>> ordinal state connection listener error', error)
        }
    })
}

export function dispatchOrdinalStateEvent(event: OrdinalStateEvent) {
    if (!event?.ordinal_id) return
    stateListeners.forEach(listener => {
        try {
            listener(event)
        } catch (error) {
            console.error('>>> ordinal state listener error', error)
        }
    })
}

/**
 * A reconnect means frames were missed while the socket was down (a laptop
 * sleeping, a PWA waking, a network blip). Consumers re-read the state they
 * care about over HTTP; the revision guard makes that idempotent.
 */
export function dispatchOrdinalStateResync() {
    resyncListeners.forEach(listener => {
        try {
            listener()
        } catch (error) {
            console.error('>>> ordinal state resync listener error', error)
        }
    })
}

function ensureSubscription() {
    if (subscription || !CableApp?.cable) return

    let connectedOnce = false
    subscription = CableApp.cable.subscriptions.create(
        // Signed-out viewers subscribe too: the public lane carries only facts
        // the wall already renders to them, and without it a sold ordinal keeps
        // showing its previous owner until they reload.
        withChannelAuth({ channel: 'OrdinalsChannel' }),
        {
            connected: () => {
                setConnected(true)
                if (connectedOnce) dispatchOrdinalStateResync()
                connectedOnce = true
            },
            disconnected: () => setConnected(false),
            rejected: () => setConnected(false),
            received: (data: OrdinalStateEvent & { message?: string }) => {
                if (data?.message !== STATE_MESSAGE) return
                dispatchOrdinalStateEvent(data)
            },
        },
    )
}

export function subscribeOrdinalState(listener: StateListener): () => void {
    stateListeners.add(listener)
    ensureSubscription()
    return () => {
        stateListeners.delete(listener)
        teardownIfIdle()
    }
}

export function subscribeOrdinalStateResync(listener: ResyncListener): () => void {
    resyncListeners.add(listener)
    ensureSubscription()
    return () => {
        resyncListeners.delete(listener)
        teardownIfIdle()
    }
}

function teardownIfIdle() {
    if (stateListeners.size || resyncListeners.size || connectionListeners.size) return
    if (!subscription) return
    subscription.unsubscribe()
    subscription = null
    connected = false
}
